import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { Table, Input, Button, Typography, message, Space, Tooltip, Tag } from "antd";
import { DeleteOutlined, DownloadOutlined, MailOutlined, ReloadOutlined } from "@ant-design/icons";
import { apiUrl } from "../../utility/Connections";
import LoadingOverlay from "../loadingOverlay/loadingOverlay";

const { Text } = Typography

const TableRelationshipsLinks = ({
    evl_relationships,
    evl_id,
    evl_title,
    refreshTable,
    pagination,
    onPageChange,
}) => {
    const [showLoading, setShowLoading] = useState(false)
    const [loadingText, setLoadingText] = useState(null)

    const [searchText, setSearchText] = useState("");

    const rootUrl = window.location.origin;

    useEffect(() => {
        // Configurar el idioma a 'es-us'
        moment.locale('es-us');
    }, []);

    const getFilters = (data, key) => {
        const uniqueValues = [...new Set(data.map(item => item[key]))];
        return uniqueValues.map(value => ({ text: value, value }));
    };

    const filteredData = evl_relationships.filter((item) =>
        Object.values(item).some((val) =>
            String(val).toLowerCase().includes(searchText.toLowerCase())
        )
    );

    const getStatusLabel = (record) => {
        if (record.status_label) {
            return record.status_label;
        }

        if (record.finished_relationships >= record.total_relationships) {
            return "Proceso finalizado";
        }

        if (record.started_relationships > 0) {
            return "Proceso iniciado";
        }

        return "Proceso sin iniciar";
    };

    const getStatusColor = (record) => {
        if (record.finished_relationships >= record.total_relationships) {
            return "green";
        }

        if (record.started_relationships > 0) {
            return "gold";
        }

        return "default";
    };

    const columns = [
        {
            title: "Gerencia",
            dataIndex: "empl_management_area_name",
            key: "empl_management_area_name",
            sorter: (a, b) => a.empl_management_area_name.localeCompare(b.empl_management_area_name),
            filters: getFilters(evl_relationships, "empl_management_area_name"),
            onFilter: (value, record) => record.empl_management_area_name === value,
        },
        {
            title: "Área",
            dataIndex: "empl_area_name",
            key: "empl_area_name",
            sorter: (a, b) => a.empl_area_name.localeCompare(b.empl_area_name),
            filters: getFilters(evl_relationships, "empl_area_name"),
            onFilter: (value, record) => record.empl_area_name === value,
        },
        {
            title: "Cargo",
            dataIndex: "empl_job_title_name",
            key: "empl_job_title_name",
            sorter: (a, b) => a.empl_job_title_name.localeCompare(b.empl_job_title_name),
            filters: getFilters(evl_relationships, "empl_job_title_name"),
            onFilter: (value, record) => record.empl_job_title_name === value,
        },
        {
            title: "Sede",
            dataIndex: "empl_organization_hq_name",
            key: "empl_organization_hq_name",
            sorter: (a, b) => a.empl_organization_hq_name.localeCompare(b.empl_organization_hq_name),
            filters: getFilters(evl_relationships, "empl_organization_hq_name"),
            onFilter: (value, record) => record.empl_organization_hq_name === value,
        },
        {
            title: "Nombre(s)",
            dataIndex: "empl_first_name",
            key: "empl_first_name",
            sorter: (a, b) => `${a.empl_first_name}`.localeCompare(`${b.empl_first_name}`),
        },
        {
            title: "Apellido(s)",
            dataIndex: "empl_last_name",
            key: "empl_last_name",
            sorter: (a, b) => `${a.empl_last_name}`.localeCompare(`${b.empl_last_name}`),
        },
        {
            title: "Identificación",
            dataIndex: "empl_dni",
            key: "empl_dni",
            sorter: (a, b) => a.empl_dni - b.empl_dni,
        },
        {
            title: (
                <Tooltip title="Total de evaluaciones asignadas">
                    TE
                </Tooltip>
            ),
            dataIndex: "total_relationships",
            key: "total_relationships",
            fixed: "right",
        },
        {
            title: (
                <Tooltip title="Total de evaluaciones iniciadas">
                    TI
                </Tooltip>
            ),
            dataIndex: "started_relationships",
            key: "started_relationships",
            fixed: "right",
        },
        {
            title: (
                <Tooltip title="Total de evaluaciones finalizadas">
                    TF
                </Tooltip>
            ),
            dataIndex: "finished_relationships",
            key: "finished_relationships",
            fixed: "right",
        },
        {
            title: "Estado",
            dataIndex: "all_finished",
            key: "all_finished",
            fixed: "right",
            render: (_, record) => {
                return (
                    <Space>
                        <Tag color={getStatusColor(record)}>
                            {getStatusLabel(record)}
                        </Tag>
                    </Space>
                )
            },
            filters: [
                { text: "Proceso sin iniciar", value: "Proceso sin iniciar" },
                { text: "Proceso iniciado", value: "Proceso iniciado" },
                { text: "Proceso finalizado", value: "Proceso finalizado" }
            ],
            onFilter: (value, record) => getStatusLabel(record) === value,
        },
        {
            title: "% Cobertura",
            dataIndex: "percent_finished",
            key: "percent_finished",
            fixed: "right",
            render: (_, record) => {
                return (
                    <Space>
                        <Text>{`${record.percent_finished}%`}</Text>
                    </Space>
                )
            }
        },
        {
            title: "Enlace",
            key: "link",
            fixed: "right",
            render: (_, record) => {
                const link = `${rootUrl}/evaluaciones/listado/${evl_id}/${record.empl_email}`;

                const handleCopy = () => {
                    navigator.clipboard.writeText(link);
                };

                const handleOpen = () => {
                    window.open(link, "_blank");
                };

                return (
                    <Space>
                        <Button size="small" onClick={handleCopy}>
                            Copiar
                        </Button>
                        <Button size="small" type="primary" onClick={handleOpen}>
                            Abrir
                        </Button>
                        <Button
                            size="small"
                            className="custom-purple-button"
                            onClick={() => sendEvaluationEmail(true, record.empl_dni)}
                        >
                            Enviar enlace
                        </Button>
                    </Space>
                );
            }
        }
    ];

    const sendTestEmail = () => {
        const token = localStorage.getItem('access_token');
        const headers = {
            headers: {
                'Authorization': `Bearer ${token}`
            },
        };

        axios.get(`${apiUrl}/emails/send-test-email/`, headers)
            .then((response) => {
                message.success("Correo de prueba enviado correctamente.");
            })
            .catch((error) => {
                message.error("Error al enviar el correo.");
            });
    }

    const sendEvaluationEmail = (isIndividual = false, empl_dni = null) => {
        setShowLoading(true);
        setLoadingText("Enviando correos, esto puede tardar un momento...");

        // Determina los evaluadores a enviar
        const evaluadoresUnicos = isIndividual
            ? [empl_dni]
            : [...new Set(evl_relationships.map(item => item.empl_dni))];

        const token = localStorage.getItem('access_token');
        const headers = {
            headers: {
                'Authorization': `Bearer ${token}`
            },
        };

        const payload = {
            evaluadores: evaluadoresUnicos,
            evl_id: evl_id,
            root_url: rootUrl,
            is_individual: isIndividual
        };

        axios.post(`${apiUrl}/emails/send-evaluation-email/`, payload, headers)
            .then((response) => {
                setShowLoading(false);
                const {
                    sent_count = 0,
                    skipped_count = 0,
                    failed_count = 0,
                } = response.data || {};

                if (failed_count > 0) {
                    message.warning(`Proceso completado. Enviados: ${sent_count}, omitidos: ${skipped_count}, fallidos: ${failed_count}.`);
                    return;
                }

                message.success(
                    isIndividual
                        ? `Correo procesado. Enviados: ${sent_count}, omitidos: ${skipped_count}.`
                        : `Proceso completado. Enviados: ${sent_count}, omitidos: ${skipped_count}.`
                );
            })
            .catch((error) => {
                setShowLoading(false);
                console.error("Error:", error);
                message.error("Error al enviar el correo.");
            });
    };

    const exportLinks = () => {
        setShowLoading(true);
        setLoadingText("Generando archivo de enlaces, esto puede tardar un momento...");

        const token = localStorage.getItem('access_token');
        const headers = {
            headers: {
                'Authorization': `Bearer ${token}`
            },
            responseType: 'blob'
        };

        const payload = {
            evl_id: evl_id,
            root_url: rootUrl
        };

        axios.post(`${apiUrl}/evaluation-relationships/export-links/`, payload, headers)
        .then((response) => {
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);

            // Obtener nombre del archivo desde header
            const contentDisposition = response.headers['content-disposition'];
            let fileName = `ENLACES ${evl_title ? evl_title.toUpperCase() : "EVALUACIÓN"}.xlsx`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="(.+)"/);
                if (match.length === 2) fileName = match[1];
            }

            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setShowLoading(false);
            message.success("Archivo descargado");
        }).catch(error => {
            setShowLoading(false);
            console.error("Error exportando el archivo:", error);
            message.error("Error exportando el archivo");
        });
    };

    return (
        <>
            {showLoading && (
                <LoadingOverlay text={loadingText} showProgress={false} progress={null} />
            )}

            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                <Input
                    placeholder="Buscar colaborador"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                    style={{ flex: 1, marginRight: 15 }}
                />
                <Button icon={<ReloadOutlined />} onClick={refreshTable}>
                    Refrescar tabla
                </Button>
            </div>

            <Table
                className="custom-green-table"
                size="small"
                columns={columns}
                dataSource={filteredData}
                rowKey="empl_id"
                pagination={{
                    current: pagination?.page || 1,
                    pageSize: pagination?.pageSize || 20,
                    total: pagination?.total || filteredData.length,
                    position: ["bottomRight"],
                    showSizeChanger: false,
                }}
                onChange={(pager) => onPageChange?.(pager.current, pager.pageSize)}
                scroll={{ x: "max-content" }}
            />

            <div className="display-center-row display-center-column align-full-center">
                <Button
                    style={{ marginBottom: 15, width: 600 }}
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={exportLinks}
                >
                    Descargar archivo de enlaces y avances de la evaluación
                </Button>
                <Button
                    style={{ width: 600 }}
                    icon={<MailOutlined />}
                    onClick={() => sendEvaluationEmail(false, null)}
                >
                    Enviar enlaces al correo a todos los evaluadores
                </Button>
            </div>
        </>
    );
};

export default TableRelationshipsLinks;
